import {
  IntegrationType,
  InternalIntegrationSetting,
  IntegrationCategory,
  ConnectionConfigType,
  IntegrationStatus,
  IntegrationEnum,
} from 'models/InternalIntegrationSetting';
import CommonTable from 'components/table/CommonTable';
import { createColumnHelper, Row } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import useServerFilteredTable from 'components/table/hooks/useServerFilteredTable';
import { API_MAX_LIMIT } from 'services/API.constants';
import { integrationsSettingsQuery } from 'pages/channels/integrationSettingsQuery';
import { useNavigate } from 'react-router-dom';
import { IntegrationsBadgeFilter } from 'pages/integrations/IntegrationsTabs.constants';
import { ChannelsTableWrapper } from './ChannelsTable.styles';
import ChannelCell from './cells/channelCell/ChannelCell';
import RateMultiplierCell from './cells/rateMultiplier/RateMultiplierCell';
import StatusCell from './cells/statusCell/StatusCell';

const ChannelsTable = ({
  title,
  isAgencyConfirmed,
  badgeFilter,
}: {
  title: string;
  isAgencyConfirmed: boolean;
  badgeFilter: IntegrationsBadgeFilter;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columnHelper = createColumnHelper<InternalIntegrationSetting>();
  const columns = [
    columnHelper.accessor('integration.name', {
      id: 'name',
      header: t('pageChannels.table.columns.channel'),
      enableSorting: true,
      cell: ChannelCell,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.accessor('integrationStats.connectedAccounts', {
      header: t('pageChannels.table.columns.connectedAccounts'),
      enableSorting: false,
      cell: ({ getValue }) => (getValue() === 0 ? '-' : getValue()),
    }),
    columnHelper.accessor('integrationStats.connectedProperties', {
      header: t('pageChannels.table.columns.connectedProperties'),
      enableSorting: false,
      cell: ({ getValue }) => (getValue() === 0 ? '-' : getValue()),
    }),
    columnHelper.display({
      header: t('pageChannels.table.columns.rateMultiplier'),
      enableSorting: false,
      cell: RateMultiplierCell,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.accessor('agencySettings.status', {
      id: 'actions',
      header: t('pageChannels.table.columns.status'),
      cell: StatusCell,
      enableSorting: true,
      meta: {
        showCellTooltip: false,
      },
    }),
  ];

  const { isLoading, tableInstance } = useServerFilteredTable({
    columns,
    getQuery: (options) =>
      integrationsSettingsQuery({
        sorting: options.sorting,
        type: IntegrationType.CHANNEL,
        category: IntegrationCategory.DIRECT_CHANNEL,
        badgeFilter,
      }),
    // The query returns all channels without pagination
    pageSize: API_MAX_LIMIT,
  });

  const redirectToChannelDetails = (
    _: React.MouseEvent<HTMLTableRowElement>,
    row: Row<InternalIntegrationSetting>,
  ) => {
    const { connectionConfig, enumId } = row.original.integration;
    const { status } = row.original.agencySettings;
    const isActive = status === IntegrationStatus.ACTIVE;

    if (!isActive) return;
    const isTypeOutboundOauth =
      connectionConfig.type === ConnectionConfigType.OUTBOUND_OAUTH;

    // Redirect to V2 details page for OutboundOauth channels: Airbnb, HomeAway, Booking.com, and HVMI
    if (isTypeOutboundOauth) {
      const V2_CHANNELS_PATHS = {
        [IntegrationEnum.BOOKING_COM]: '/channels/bookingdotcom/sync',
        [IntegrationEnum.HVMI]: '/channels/default/sync?chname=HVMB',
        [IntegrationEnum.AIRBNB]: '/channels/airbnb/sync',
        [IntegrationEnum.HOMEAWAY]: '/channels/vrbo/sync',
      };

      const path = V2_CHANNELS_PATHS[enumId as keyof typeof V2_CHANNELS_PATHS];

      navigate(path);
    } else {
      navigate(`/channels/details/${enumId}`);
    }
  };

  const getRowClassName = (row: Row<InternalIntegrationSetting>) => {
    const { status } = row.original.agencySettings;
    const { allowedForAgencyOnly } = row.original.integration;

    const isActive = status === IntegrationStatus.ACTIVE;
    const hasPropertyLevelPage = !allowedForAgencyOnly;

    if (!isActive) {
      return undefined;
    }

    if (hasPropertyLevelPage) {
      return 'clickable-channel-row';
    }

    return undefined;
  };

  return (
    <ChannelsTableWrapper>
      <h2>{title}</h2>
      <CommonTable<InternalIntegrationSetting>
        tableInstance={tableInstance}
        isLoading={isLoading}
        data-testid={`channels-table-${
          badgeFilter === IntegrationsBadgeFilter.WITH_BADGES
            ? 'featured'
            : 'others'
        }`}
        handleRowClick={
          isAgencyConfirmed ? redirectToChannelDetails : undefined
        }
        getRowClassName={getRowClassName}
      />
    </ChannelsTableWrapper>
  );
};

export default ChannelsTable;
